import { Component, OnDestroy, OnInit, signal, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { LanguageType } from '../../../../shared/constants/language-eum';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthState } from '../../../../shared/store/auth/auth.state';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { UserService } from '../../../modules/admin-modules/administrator/users/user.service';
import { LOCAL_STORAGE } from '../../../../shared/constants/localstorage-enum';
import { UserType, UserTypeLabel } from '../../../../shared/constants/user-type-enum';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../../shared/services/common/common-notification.service';
import { EnumService } from '../../../../shared/services/common/enum.service';
import { NULL_VIEW_SYMBOL, PATIENT_PORTAL_ROUTE_PREFIX } from '../../../../shared/constants/system-constants.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { DynamicLanguageFieldPipe } from "../../../../shared/pipes/language-translate.pipe";
import { ROUTE_USER_INFO, SLUG_NOTIFICATION } from '../../../../shared/constants/route-consts';
import { LanguageToggleService } from '../../../../shared/services/translation/language-toggle.service';
import { TimeDifferencePipe } from "../../../../shared/pipes/time-difference.pipe";
import { NotificationUnion } from '../../../../shared/models/notification.model';
import { RelativeLoaderDirective } from '../../../../shared/directives/relative-loader/relative-loader.directive';
import { BreadcrumbComponent } from "../../../../shared/components/breadcrumb/breadcrumb.component";
import { FormService } from '../../../modules/admin-modules/pre-auth/preauth-request/services/form.service';

@Component({
  selector: 'app-admin-panel-header',
  standalone: true,
  imports: [
    MatButtonModule,
    MatBadgeModule,
    MatMenuModule,
    RouterModule,
    TranslateModule,
    RelativeLoaderDirective,
    CommonModule,
    MatTooltipModule,
    MatRippleModule,
    DynamicLanguageFieldPipe,
    TimeDifferencePipe,
    BreadcrumbComponent
  ],
  templateUrl: './admin-panel-header.component.html',
  styleUrl: './admin-panel-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminPanelHeaderComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private enumService = inject(EnumService);
  private languageToggleService = inject(LanguageToggleService);
  private formService = inject(FormService);

  @ViewChild('selectMenuTrigger') selectMenuTrigger!: MatMenuTrigger;
  @ViewChild('notificationMenuTrigger') notificationMenuTrigger!: MatMenuTrigger;
  onDestroy$: Subject<void> = new Subject<void>();
  notifications$: Observable<{ result, trigger?: boolean; }>;
  currentLang = signal<LanguageType>(LanguageType.English);
  userFullName$!: Observable<string>;
  userType$!: Observable<number>;
  userFullName: string;
  userTypeName: string;
  userType: number;
  userTypes = UserType;
  isNotificationLoading = false;

  constructor() {
    this.userFullName$ = this.store.select(AuthState.fullName);
    this.userType$ = this.store.select(AuthState.userType);
    const language = localStorage.getItem(LOCAL_STORAGE.CURRENT_LANGUAGE);
    this.currentLang.set(<LanguageType>language);
  }

  ngOnInit() {
    this.userFullName$.pipe(takeUntil(this.onDestroy$)).subscribe(fullName => this.userFullName = fullName);
    this.userType$.pipe(takeUntil(this.onDestroy$)).subscribe(usertType => {
      if (usertType) {
        this.userType = usertType;
        this.notificationService.fetchNotifications(true);
        this.userTypeName = this.enumService.getLabelByMappedValue(UserType, UserTypeLabel, usertType);

        if (this.userType === UserType.TPA) {
          this.notifications$ = this.notificationService.tpaNotifications$.asObservable()
            .pipe(map(data => ({ ...data, result: data.result.filter(n => !n.isRead) })));
        } else
          this.notifications$ = this.notificationService.notifications$.asObservable();
      } else
        this.userTypeName = NULL_VIEW_SYMBOL;
    });
  }

  toggleSidebar(event: MouseEvent) {
    if (window.innerWidth < 991) {
      const body = document.body;
      body.classList.add('show-sidebar');
      event.preventDefault();
    } else {
      if (document.body.classList.contains('sidebar-full')) {
        document.body.classList.remove('sidebar-full');
        document.body.classList.add('sidebar-collapsed');
        this.formService.setCollapsed(true);
      } else {
        document.body.classList.add('sidebar-full');
        document.body.classList.remove('sidebar-collapsed');
        this.formService.setCollapsed(false);
      }
    }
  }

  logout(): void {
    this.userService.logOutDialog();
  }

  onMyProfileClick() {
    this.router.navigate([ROUTE_USER_INFO]);
  }

  onChangePasswordClick() {
    this.userService.openPasswordDialog(this.userService.loggedInUser()?.id, true);
  }

  markAllAsRead() {
    this.isNotificationLoading = true;
    this.notificationService.markAllAsRead(null, () => this.stopNotificationLoading(), true, true);
    this.selectMenuTrigger?.closeMenu();
  }

  markAsRead(notification: NotificationUnion) {
    this.isNotificationLoading = true;
    this.notificationService.markReadNotification(notification, null, null, () => this.stopNotificationLoading(), (this.userType !== UserType.Patient));
  }

  markAsReadAndNavigateToEntity(notification: NotificationUnion) {
    notification.navigateToEntity = true;
    this.markAsRead(notification);
  }

  stopNotificationLoading = () => {
    this.isNotificationLoading = false;
  };

  routeToNotificationCenter() {
    this.router.navigate([(this.userType == UserType.Patient ? PATIENT_PORTAL_ROUTE_PREFIX : '') + SLUG_NOTIFICATION]);
  }

  formatNotificationMessage(description: string): string {
    return this.notificationService.formatNotificationMessage(description);
  }

  showMarkAllRead(notifications): boolean {
    if (!notifications?.length) return false;

    if (this.userType === UserType.TPA) {
      const providerUpdate = this.notificationService.getPermission(UserType.Provider)?.update;
      const payerUpdate = this.notificationService.getPermission(UserType.Payer)?.update;
      return notifications.some(n => (n.providerNotificationId && providerUpdate) || (n.payerNotificationId && payerUpdate));
    }
    return this.notificationService.getPermission(this.userType)?.update;
  }

  showMarkRead(notification): boolean {
    if (this.userType === UserType.TPA) {
      const payerPermission = this.notificationService.getPermission(UserType.Payer);
      const providerPermission = this.notificationService.getPermission(UserType.Provider);

      return ((notification.providerNotificationId && providerPermission?.update) || (notification.payerNotificationId && payerPermission?.update));
    }
    return this.notificationService.getPermission(this.userType)?.update;
  }

  setLanguage(lang: string) {
    this.languageToggleService.switchLanguage(lang as LanguageType, this.currentLang());
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
